import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IonButtons,
  IonIcon,
  IonPage,
  IonSpinner,
  isPlatform,
  useIonAlert,
  useIonRouter,
  useIonViewDidEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import {
  AppHeader,
  Avatar,
  ChatMessage,
  ChatMessageSkeleton,
  Container,
  EmptyChatMessages,
} from '@mindly/ui-components';
import { useHistory } from 'react-router-dom';
import { RouteComponentProps } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { uid } from 'uid';
import { Browser } from '@capacitor/browser';

import { setIsActive } from '../../redux/tabBar/reducer';
import { addMessage, MESSAGES_PER_PAGE, resetOffset } from '../../redux/chatMessages/reducer';
import { userSelector } from '../../redux/user/selector';
import {
  selectIsInfiniteSelectorByRoomId,
  selectorIsLoading,
  selectSortedMessagesByRoom,
} from '../../redux/chatMessages/selector';
import { MessageTypes, TextMessage } from '../types';
import useAgoraRtm from '../../context/AgoraContext/AgoraContext';
import {
  BackButton,
  EmptyWrapper,
  HeaderInfoTitle,
  RoomTitle,
  StyledIonContent,
  StyledSpinner,
} from './ChatRoomPage.style';
import { selectRoomDataBySenderId } from '../../redux/chatRooms/selector';
import { addChatNewMessage, fetchChatMessages, setMessageViewed } from '../../redux/chatMessages/thunk';
import { fetchBroadcastChatRoom, fetchChatRoomsBySpecialistID } from '../../redux/chatRooms/thunk';
import { createRoom } from '../../redux/chatRooms/reducer';
import { PrivateRoutes, PublicRoutes } from 'src/routes/navigation';
import { psychologistSelectorById } from '../../redux/specialists/selector';
import { chevronBackOutline } from 'ionicons/icons';
import { lastActiveSubscriptionSelector } from '../../redux/subscriptions/selector';
import { consultationsSelector } from '../../redux/consultations/selector';
import { loadConsultationsThunk } from '../../redux/consultations/action';
import { fetchSubscriptions } from '../../redux/subscriptions/thunk';
import { FormattedMessage, useIntl } from 'react-intl';
import useLangs from '../../context/LangsContext';
import { Keyboard } from '@capacitor/keyboard';
import useToast from '../../context/ToastContext/ToastContext';
import { TranslatedTextarea } from '../../translated-components';
import { MindlyCircleLogo35x35 } from '../../shared/assets/icons/mindlyCircleLogo';
import { CHAT_ROOM_TYPES } from '../../redux/chatRooms/types';
import { BROADCAST_MESSAGE_SENDER_ID } from '../../redux/chatMessages/types';

interface ChatRoomPageProps
  extends RouteComponentProps<{
    id: string;
  }> {}

const MAX_FILE_SIZE_BYTES = 10485760;
const MAX_FILE_SIZE = MAX_FILE_SIZE_BYTES / 1024 / 1024;

export const ChatRoomPage: FC<ChatRoomPageProps> = ({ match }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [present] = useIonAlert();
  const intl = useIntl();
  const { locale } = useLangs();

  const roomId = match.params.id;
  // const { pushToAppointmentPage } = useAppointment();
  const currentUser = useSelector(userSelector);
  const specialistData = useSelector(psychologistSelectorById(roomId));
  const roomData = useSelector(selectRoomDataBySenderId(roomId));
  const messages = useSelector(selectSortedMessagesByRoom(roomData?.room.id ?? ''));
  const isDadaLoading = useSelector(selectorIsLoading);
  const isInfiniteDisabled = useSelector(selectIsInfiniteSelectorByRoomId(roomData?.room.id ?? ''));
  const activeSubscription = useSelector(lastActiveSubscriptionSelector(match.params.id));
  const consultations = useSelector(consultationsSelector);

  const virtuosoRef = useRef<VirtuosoHandle | null>(null);
  const footerRef = useRef<HTMLIonFooterElement | null>(null);
  const contentRef = useRef<HTMLIonContentElement | null>(null);

  const { isLoggedIn, isLoading, loginAgoraClient, agoraClient, sendAgoraMessage } = useAgoraRtm();
  const [isKeyboardOpened, setKeyboardOpenet] = useState(false);
  const [isPageView, setIsPageView] = useState(false);
  const [isLoadNewData, setLoadNewData] = useState(false);
  const [firstItemIndex, setFirstItemIndex] = useState(1000000000);
  const [initialContainerHeight, setInitialContainerHeight] = useState(0);
  const navigation = useIonRouter();

  useEffect(() => {
    const fetchData = async () => {
      if (isLoading) return;

      if (roomData?.room.id) {
        dispatch(resetOffset(roomData.room.id));
        await dispatch(fetchChatMessages(roomData.room.id));
      }
    };
    fetchData();
  }, [currentUser?.id, roomData?.room.id, isLoading]);

  useEffect(() => {
    const fetchRoomData = async () => {
      if (!roomData?.room.id) {
        if (roomId === BROADCAST_MESSAGE_SENDER_ID) {
          await dispatch(fetchBroadcastChatRoom(currentUser?.id ?? ''));
        } else {
          await dispatch(
            fetchChatRoomsBySpecialistID({
              clientID: currentUser?.id ?? '',
              specialistId: roomId,
            })
          );
        }
      }
    };

    fetchRoomData();
  }, [roomData?.room.id]);

  useEffect(() => {
    if (activeSubscription && consultations) return;
    Promise.all([dispatch(loadConsultationsThunk(currentUser?.id)), dispatch(fetchSubscriptions(currentUser?.id))]);
  }, []);

  useIonViewDidEnter(() => {
    if (!currentUser?.id) {
      history.push(PublicRoutes.AUTH);
      return;
    }
    dispatch(setIsActive(false));
    setIsPageView(true);
    if (virtuosoRef.current) {
      virtuosoRef.current?.autoscrollToBottom();
    }
    setInitialContainerHeight(contentRef.current?.clientHeight ?? 0);
  });

  useIonViewWillLeave(() => {
    // with animation useIonViewWillLeave in this page is called after useIonViewWillEnter in SpecialistProfilePage
    // so we need to check if the route where we are going is not the specialist profile page
    if (
      !navigation.routeInfo.pathname.startsWith(PrivateRoutes.PSYCHOLOGIST_PROFILE_TAB.replace('/:id', '')) &&
      !navigation.routeInfo.pathname.startsWith(PrivateRoutes.SESSION_DETAIL.replace('/:id', ''))
    ) {
      dispatch(setIsActive(true));
    }
    setIsPageView(false);
  }, [navigation.routeInfo]);

  useEffect(() => {
    if (!isPlatform('hybrid')) return;
    if (!isPlatform('ios')) return;

    Keyboard.addListener('keyboardDidShow', (info) => {
      setKeyboardOpenet(true);
      if (contentRef.current?.clientHeight && initialContainerHeight - contentRef.current?.clientHeight >= 200) return;

      footerRef.current?.style.setProperty('transform', `translate3d(0, -${info.keyboardHeight}px, 0)`);
    });

    Keyboard.addListener('keyboardWillHide', () => {
      setKeyboardOpenet(false);
      footerRef.current?.style.removeProperty('transform');
    });

    return () => {
      isPlatform('hybrid') && Keyboard.removeAllListeners();
    };
  }, [footerRef, contentRef, initialContainerHeight]);

  const handleMessageSend = useCallback(
    async (message: string | File | null, messageType = 'text', fileInfo = {}) => {
      if (!message) return;

      if (!currentUser?.id) return;

      if (message instanceof File && message.size > MAX_FILE_SIZE_BYTES) {
        present({
          mode: 'ios',
          message: intl.formatMessage(
            {
              id: 'general.file.validation.size',
              defaultMessage: 'Розмір файлу має бути не більше {size} мб.',
            },
            { size: MAX_FILE_SIZE }
          ),
          buttons: ['OK'],
        });
        return;
      }

      let createdMessageData;

      const room_id = roomData?.room.id ? roomData?.room.id : uid(20);
      const message_id = uid(20);
      const updated_at = Date.now();

      dispatch(
        createRoom({
          avatar: roomData?.avatar ? roomData.avatar : '',
          name: roomData?.name ? roomData.name : '',
          room: {
            id: room_id,
            client_id: currentUser.id,
            specialist_id: roomId,
            updated_at,
          },
          last_message: {
            id: message_id,
            room_id,
            sender_id: currentUser.id,
            receiver_id: roomId,
            message,
            updated_at,
            status: 'loading',
            messageType,
            fileInfo,
          },
        })
      );

      dispatch(
        addMessage({
          id: message_id,
          room_id,
          sender_id: currentUser.id,
          receiver_id: roomId,
          message,
          updated_at,
          status: 'loading',
          messageType,
          fileInfo,
        })
      );

      try {
        createdMessageData = await dispatch(
          addChatNewMessage({
            sender_id: currentUser.id,
            receiver_id: roomId,
            sender_type: 'client',
            message,
            room_id,
            message_id,
            updated_at,
            messageType,
            fileInfo,
            locale,
          })
        ).unwrap();
        dispatch(createRoom(createdMessageData));
      } catch (error) {
        showToast({ error });
      }

      const sendDataMessage: TextMessage = {
        type: MessageTypes.MESSAGE,
        ...createdMessageData,
        avatar: Array.isArray(currentUser.avatar)
          ? currentUser.avatar[currentUser.avatar.length - 1].url
          : currentUser.avatar,
        name: `${currentUser.display_name} ${currentUser.last_name}`,
      };

      await sendAgoraMessage(sendDataMessage, roomId);
    },
    [roomData, currentUser, roomId, sendAgoraMessage, intl]
  );

  const loadMessagesData = useCallback(async () => {
    if (!roomData?.room.id) return;

    if (isInfiniteDisabled) return;

    if (isLoadNewData) return;

    if (messages.length < MESSAGES_PER_PAGE) return;

    setLoadNewData(true);
    const newMessages = await dispatch(fetchChatMessages(roomData.room.id)).unwrap();
    if (newMessages.length) {
      setFirstItemIndex(() => firstItemIndex - newMessages.length);
    }
    setLoadNewData(false);
  }, [
    dispatch,
    roomData?.room.id,
    MESSAGES_PER_PAGE,
    firstItemIndex,
    isInfiniteDisabled,
    fetchChatMessages,
    isLoadNewData,
    messages.length,
  ]);

  const isFirstLoading = useMemo(
    () => isDadaLoading === 'pending' && !messages.length,
    [isDadaLoading, messages.length]
  );

  const isBroadcastRoom = useMemo(() => {
    return roomData?.room.type === CHAT_ROOM_TYPES.MINDLY_BROADCAST_ROOM;
  }, [roomData?.room?.type]);

  const handleViewMessage = useCallback(
    async (message_id, room_id) => {
      dispatch(setMessageViewed(message_id));

      try {
        if (!isLoggedIn) {
          await loginAgoraClient();
        }

        await agoraClient?.sendMessageToPeer(
          {
            text: JSON.stringify({
              type: MessageTypes.VIEWED_MESSAGE,
              message_id,
              room_id,
            }),
          },
          roomId
        );
      } catch (e) {}
    },
    [dispatch, setMessageViewed, isLoggedIn, loginAgoraClient, currentUser?.id, agoraClient]
  );

  const handleFileClick = useCallback(
    async (type, link) => {
      if (type === 'file') {
        await Browser.open({
          url: link,
          presentationStyle: 'popover',
        });
      }
    },
    [Browser]
  );

  let content;
  if (isFirstLoading) {
    content = <ChatMessageSkeleton />;
  } else {
    content = (
      <>
        {(!messages || messages.length === 0) && (
          <EmptyWrapper>
            <EmptyChatMessages
              title={intl.formatMessage({
                id: 'chat.empty.start.title',
                defaultMessage: 'Почни розмову',
              })}
              text={intl.formatMessage({
                id: 'chat.empty.start.text',
                defaultMessage: 'Надішли повідомлення психологу. Розкажи про свій запит та запитай про методи терапії.',
              })}
            />
          </EmptyWrapper>
        )}
        {isPageView && !!messages.length && (
          <Virtuoso
            ref={virtuosoRef}
            style={{
              flex: 1,
            }}
            components={{
              Header: () =>
                isLoadNewData ? (
                  <div style={{ display: 'flex' }}>
                    <StyledSpinner name="crescent" />
                  </div>
                ) : null,
            }}
            data={messages}
            startReached={loadMessagesData}
            followOutput="smooth"
            alignToBottom
            overscan={{ main: 4000, reverse: 4000 }}
            firstItemIndex={firstItemIndex}
            initialTopMostItemIndex={{
              align: 'end',
              index: messages.length - 1,
            }}
            itemsRendered={(items) => {
              items.forEach((item) => {
                if (item?.data?.status !== 'viewed' && item?.data?.receiver_id === currentUser?.id) {
                  item?.data?.id && handleViewMessage(item.data.id, item.data.room_id);
                }
              });
            }}
            itemContent={(index, item) => {
              const realIndex = messages.findIndex((msg) => msg.id === item.id);
              const lastItem = realIndex === messages.length - 1;

              return (
                <div
                  style={{
                    padding:
                      lastItem && item.messageType === 'broadcast'
                        ? `3px 9px ${isPlatform('ios') ? '40px' : '20px'} 9px`
                        : '3px 9px',
                  }}
                  key={`${item.id}_${item.sender_id === roomId ? item.sender_id : item.receiver_id}`}
                >
                  <ChatMessage
                    position={item.sender_id === roomId ? 'left' : 'right'}
                    message={item.message}
                    fileUrl={item?.file_url ?? undefined}
                    time={item.updated_at}
                    timeZone={currentUser?.time_zone}
                    status={item.status}
                    messageType={item.messageType}
                    fileInfo={item.fileInfo}
                    onFileClick={handleFileClick}
                    locale={locale}
                    buttons={item.buttons?.map((el) => ({
                      text: el.text,
                      onClickHandler: () => {
                        const routePattern = /^\/[^/].*/;
                        if (routePattern.test(el.link)) {
                          history.push(el.link);
                        } else {
                          Browser.open({
                            url: el.link,
                            presentationStyle: 'fullscreen',
                            windowName: '_self',
                          });
                        }
                      },
                    }))}
                  />
                </div>
              );
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <IonPage>
        <AppHeader>
          <IonButtons slot="start">
            <BackButton
              onClick={() => {
                history.go(-1);
              }}
            >
              <IonIcon icon={chevronBackOutline} />
            </BackButton>
          </IonButtons>
          <RoomTitle>
            {isLoading ? (
              <div>
                <IonSpinner name="crescent" />
                <FormattedMessage id={'chat.loading.text'} defaultMessage={'Оновлення...'} />
              </div>
            ) : (
              <HeaderInfoTitle>
                {!isBroadcastRoom ? (
                  <Avatar
                    dimensions={35}
                    withBorder={false}
                    src={roomData?.avatar ?? specialistData?.profile_photo_link ?? ''}
                    firstName={roomData?.name ?? specialistData?.first_name}
                    onClick={() => {
                      if (!isBroadcastRoom) {
                        history.push(PrivateRoutes.PSYCHOLOGIST_PROFILE.replace(':id', roomId));
                      }
                    }}
                  />
                ) : (
                  <MindlyCircleLogo35x35 />
                )}

                {(roomData?.name ?? `${specialistData?.first_name} ${specialistData?.last_name}`)
                  .split(' ')
                  .map((item, index) => (index > 0 ? `${item[0]}.` : item))
                  .slice(0, 2)
                  .join(' ')}
              </HeaderInfoTitle>
            )}
          </RoomTitle>
          {/* TODO: Attention Required: Resolve identified issues when developing the new chat functionality. */}
          {/* Note: Due to the absence of current specialist data, accurate status computation is not feasible. */}
          {/*<IonButtons slot="end">*/}
          {/*  <CalendarButton*/}
          {/*    disabled={!consultations || !subscriptions || !specialistData}*/}
          {/*    onClick={() => pushToAppointmentPage(roomId)}*/}
          {/*  >*/}
          {/*    <IonIcon icon={calendarOutline} />*/}
          {/*  </CalendarButton>*/}
          {/*</IonButtons>*/}
        </AppHeader>
        <StyledIonContent ref={contentRef}>
          <Container style={{ padding: '0' }}>{content}</Container>
        </StyledIonContent>
        {!isBroadcastRoom && (
          <TranslatedTextarea
            ref={footerRef}
            onSend={handleMessageSend}
            onError={(message) => {
              present({
                mode: 'ios',
                message: intl.formatMessage(
                  {
                    id: 'error.validation.maxTextSize',
                    defaultMessage: `Максимальна кількість символів в одному повідомленні не повинна перевищувати 7000. Зараз у вас {length} символів, розділіть повідомлення на частини.`,
                  },
                  { length: message.length }
                ),
                buttons: ['OK'],
              });
            }}
            isOpen={isKeyboardOpened}
            maxLength={7000}
            onFileSelect={handleMessageSend}
          />
        )}
      </IonPage>
    </>
  );
};
